import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface DonutSlice {
    label: string;
    /** Share of the chart as a percentage 0–100. All slices should sum to 100. */
    percentage: number;
    count: number;
    /** MUI palette path ('success.main') or CSS color ('#4caf50'). */
    color: string;
}

interface DonutChartProps {
    slices: DonutSlice[];
    /** Outer diameter in px. Default: 160. */
    size?: number;
    /** Ring thickness in px. Default: 22. */
    ringWidth?: number;
    /** Large text shown in the center hole. */
    centerValue?: string;
    /** Small caption below centerValue. */
    centerLabel?: string;
    /** Color of centerValue — MUI palette path or CSS color. Default: inherited text color. */
    centerColor?: string;
    /** Hide the slice legend (e.g. single-value gauges). Default: false. */
    hideLegend?: boolean;
}

// Turns an MUI palette path such as 'success.main' into a real CSS colour, because a
// conic-gradient can only take real colours. Values that are already CSS colours are
// returned unchanged.
const resolvePaletteColor = (theme: import('@mui/material').Theme, colorStr: string): string => {
    const [section, shade] = colorStr.split('.');
    if (shade) {
        const entry = (theme.palette as unknown as Record<string, Record<string, string>>)[section];
        return entry?.[shade] ?? colorStr;
    }
    return colorStr;
};

const buildGradient = (theme: import('@mui/material').Theme, slices: DonutSlice[]): string => {
    let cursor = 0;
    const stops = slices.map((slice) => {
        const from = cursor;
        const to = cursor + slice.percentage;
        cursor = to;
        const color = resolvePaletteColor(theme, slice.color);
        return `${color} ${from}% ${to}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
};

const LegendRow: React.FC<{ slice: DonutSlice }> = ({ slice }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: '64px auto',
            columnGap: 1,
            alignItems: 'center',
        }}
    >
        <Stack direction="row" spacing={1} alignItems="center">
            <Box
                sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: slice.color,
                    flexShrink: 0,
                }}
            />

            <Typography variant="body2">{slice.label}</Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
            {Math.round(slice.percentage)}% ({slice.count.toLocaleString()})
        </Typography>
    </Box>
);

const DonutChart: React.FC<DonutChartProps> = ({
    slices,
    size = 160,
    ringWidth = 22,
    centerValue,
    centerLabel,
    centerColor,
    hideLegend = false,
}) => (
    <Stack direction="row" spacing={3} alignItems="center">
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: (theme) => buildGradient(theme, slices),
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: ringWidth,
                    left: ringWidth,
                    width: size - ringWidth * 2,
                    height: size - ringWidth * 2,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {centerValue && (
                    <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: centerColor }}>{centerValue}</Typography>
                )}
                {centerLabel && (
                    <Typography variant="caption" color="text.secondary">
                        {centerLabel}
                    </Typography>
                )}
            </Box>
        </Box>

        {!hideLegend && (
            <Stack spacing={1}>
                {slices.map((slice) => (
                    <LegendRow key={slice.label} slice={slice} />
                ))}
            </Stack>
        )}
    </Stack>
);

export default DonutChart;
